import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { useUpdateTagMutation } from "@/modules/mine";
import type { DialogComponentProps } from "@/shared/components/dialog";
import { Button } from "@/shared/ui/button";
import { DialogFooter } from "@/shared/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";

type EditTagResult = {
  action: "updated";
};

interface EditTagProps extends DialogComponentProps<EditTagResult> {
  tag: {
    id: string;
    name: string;
  };
}

const formSchema = z.object({
  name: z.string()
    .min(1, { message: "标签名称不能为空" })
    .max(30, { message: "标签名称不能超过30个字符" }),
});

export function EditTag({ close, resolve, tag }: EditTagProps) {
  const updateTagMutation = useUpdateTagMutation();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: tag.name,
    },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    updateTagMutation.mutate(
      {
        id: tag.id,
        name: values.name.trim(),
      },
      {
        onSuccess: () => {
          toast.success("标签更新成功");
          resolve({ action: "updated" });
        },
      },
    );
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs text-muted-foreground">标签名称</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="请输入标签名称" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={close}
          disabled={updateTagMutation.isPending}
        >
          取消
        </Button>
        <Button
          type="button"
          onClick={form.handleSubmit(onSubmit)}
          disabled={updateTagMutation.isPending}
        >
          确认
          {updateTagMutation.isPending && <Loader2 className="animate-spin" />}
        </Button>
      </DialogFooter>
    </>
  );
}
